import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  TrendingUp,
  Download,
  Calendar,
  DollarSign,
  ShoppingBag,
  CreditCard,
  FileText,
  ArrowLeft,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminSalesReports() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Set default dates (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateTo(today.toISOString().split("T")[0]);
    setDateFrom(thirtyDaysAgo.toISOString().split("T")[0]);

    // Load initial report
    fetchReport(
      thirtyDaysAgo.toISOString().split("T")[0],
      today.toISOString().split("T")[0]
    );
  }, []);

  const fetchReport = async (from, to) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/super-admin/reports/sales`,
        {
          date_from: from || dateFrom,
          date_to: to || dateTo,
        },
        { headers: getAdminAuthHeader() }
      );
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to load sales report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Please select date range");
      return;
    }
    fetchReport(dateFrom, dateTo);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const getTopHours = () => {
    if (!reportData?.hourly_trends) return [];
    return [...reportData.hourly_trends]
      .sort((a, b) => b.total_revenue - a.total_revenue)
      .slice(0, 5);
  };

  return (
    <SuperAdminLayout>
      <div>
        {/* Page Header */}
        <div
          className="mb-8 flex w-full items-center px-6
  bg-gradient-to-r from-orange-500 to-red-500
  shadow-md text-white py-[1.65rem]"
        >
          <Button
            onClick={() => navigate("/admin/dashboard")}
            variant="ghost"
            className="hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Sales Reports</h1>
            <p className="mt-1">
              Comprehensive sales analytics across all outlets
            </p>
          </div>
        </div>
        <div className="p-8">
          {/* Date Range Selector */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    placeholder="From"
                  />
                </div>
                <span className="text-gray-500">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="To"
                />
                <Button
                  onClick={handleGenerateReport}
                  disabled={loading}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Generate Report
                </Button>
                {/* <Button variant="outline" disabled>
                  <Download className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
                <Button variant="outline" disabled>
                  <FileText className="h-4 w-4 mr-2" />
                  Export PDF
                </Button> */}
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">Loading report data...</p>
            </div>
          ) : reportData ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Total Revenue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(reportData.total_revenue)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Gross sales</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Total Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">
                      {reportData.total_orders}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Completed orders
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Commission Earned
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">
                      {formatCurrency(reportData.total_commission)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Platform earnings
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">
                      Avg Order Value
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">
                      {formatCurrency(reportData.avg_order_value)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Per order</p>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Mode Breakdown */}
              {reportData.payment_mode_breakdown &&
                reportData.payment_mode_breakdown.length > 0 && (
                  <Card className="mb-8">
                    <CardHeader>
                      <CardTitle>Payment Mode Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {reportData.payment_mode_breakdown.map((mode) => (
                          <div
                            key={mode.payment_mode}
                            className="border rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 capitalize">
                                {mode.payment_mode}
                              </span>
                              <CreditCard className="h-4 w-4 text-gray-400" />
                            </div>
                            <div className="text-2xl font-bold text-gray-900">
                              {formatCurrency(mode.total_amount)}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {mode.order_count} orders (
                              {mode.percentage.toFixed(1)}%)
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Top Performing Hours */}
              {reportData.hourly_trends && (
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle>Top 5 Peak Hours</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {getTopHours().map((hourData) => (
                        <div
                          key={hourData.hour}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <span className="font-semibold text-gray-900">
                              {hourData.hour}:00 - {hourData.hour + 1}:00
                            </span>
                            <span className="text-sm text-gray-500 ml-2">
                              ({hourData.total_orders} orders)
                            </span>
                          </div>
                          <div className="text-lg font-bold text-green-600">
                            {formatCurrency(hourData.total_revenue)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Cancelled Orders */}
              {reportData.cancelled_orders > 0 && (
                <Card className="mb-8">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-600" />
                      Cancelled Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Total Cancelled</p>
                        <p className="text-2xl font-bold text-red-600">
                          {reportData.cancelled_orders}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Revenue Lost</p>
                        <p className="text-2xl font-bold text-red-600">
                          {formatCurrency(reportData.cancelled_revenue)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <TrendingUp className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No Data Available
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Select a date range and click "Generate Report" to view sales
                  analytics.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
